import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../../../../../api';

const AddTransferCaseModal = ({ show, onHide, refreshList }) => {
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [filteredTransferTo, setFilteredTransferTo] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [fileInputs, setFileInputs] = useState([0]);
  const [showScanner, setShowScanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [medicalCenterId, setMedicalCenterId] = useState(null);

  // Initialize form data with clean state
  const initialFormData = {
    name: '',
    age: '',
    gender: '',
    bracelet_id: '',
    diagnose: '',
    doctor: '',
    doctor_name: '',
    clinic_no: '',
    request_details: '',
    escort_team: {
      escort_physician: '',
      escort_nurse: '',
      escort_paramedic: '',
      other: ''
    },
    documents: {}
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const MAX_FILES = 3;

  // Reset everything when modal opens/closes
  useEffect(() => {
    if (show) {
      // Reset all states when modal opens
      setFormData(initialFormData);
      setErrors({});
      setTransferFrom('');
      setTransferTo('');
      setDoctors([]);
      setFileInputs([0]);
      setShowScanner(false);

      const role = sessionStorage.getItem('Role_Name');
      const medId = sessionStorage.getItem('Medical_Center_Id') || sessionStorage.getItem('medical_center_id');
      setUserRole(role || '');
      setMedicalCenterId(medId || null);

      const fetchCenters = async () => {
        try {
          const res = await axiosInstance.get('/ems/medical-centers');
          const centers = Array.isArray(res.data) ? res.data : res.data.data || [];
          setMedicalCenters(centers);

          if ((role === 'Hospital Representative (Nurse)' || role === 'EMS Resource Manager') && medId && medId !== '0') {
            const userCenter = centers.find(c => c.id === parseInt(medId));
            if (userCenter) {
              setTransferFrom(String(userCenter.id));
              setFilteredTransferTo(centers.filter(c => c.id !== userCenter.id));
            }
          } else {
            setFilteredTransferTo(centers);
          }
        } catch (err) {
          console.error('Failed to load medical centers', err);
        }
      };
      fetchCenters();
    }
  }, [show]);

  // Update filtered "Transfer To" when "Transfer From" changes
  useEffect(() => {
    if (transferFrom && medicalCenters.length > 0) {
      setFilteredTransferTo(medicalCenters.filter(c => c.id !== parseInt(transferFrom)));
    }
  }, [transferFrom, medicalCenters]);

  // Fetch doctors when transferFrom changes
  useEffect(() => {
    if (!transferFrom) {
      setDoctors([]);
      return;
    }
    const fetchDoctors = async () => {
      try {
        const res = await axiosInstance.get(`/ems/get-specefic-medical-center-doctors-list/${transferFrom}`);
        const list = Array.isArray(res.data) ? res.data : [];
        setDoctors(list.map(d => ({
          id: d.id,
          name: `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Unnamed Doctor'
        })));
      } catch (err) {
        setDoctors([]);
      }
    };
    fetchDoctors();
  }, [transferFrom]);

  // QR Code Scanner
  useEffect(() => {
    if (!showScanner) return;

    const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 }, false);

    const success = (decodedText) => {
      setFormData(prev => ({ ...prev, bracelet_id: decodedText }));
      setErrors(prev => ({ ...prev, bracelet_id: '' })); // Clear error instantly
      setShowScanner(false);
      scanner.clear();
    };

    const error = (err) => console.warn(err);

    scanner.render(success, error);

    return () => {
      scanner.clear().catch(() => {});
    };
  }, [showScanner]);

  // Generic change handler with instant error clearing
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, name: '' }));
      } else {
        setErrors(prev => ({ ...prev, name: 'Name can only contain letters and spaces' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEscortTeamChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      escort_team: { ...prev.escort_team, [name]: value }
    }));
  };

  const handleDoctorChange = (e) => {
    const id = e.target.value;
    const doctor = doctors.find(d => d.id === parseInt(id));
    setFormData(prev => ({
      ...prev,
      doctor: id,
      doctor_name: doctor ? doctor.name : ''
    }));
    if (errors.doctor) setErrors(prev => ({ ...prev, doctor: '' }));
  };

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jfif', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'Only PDF, JPG, PNG, WEBP allowed' }));
      return;
    }
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'File must be under 5MB' }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [`documents${index + 1}`]: file }
    }));
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '', documents: '' }));
  };

  const addFileInput = () => {
    if (fileInputs.length < MAX_FILES) {
      setFileInputs(prev => [...prev, prev.length]);
    }
  };

  const removeFileInput = (index) => {
    setFileInputs(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => {
      const key = `documents${index + 1}`;
      const { [key]: _, ...rest } = prev.documents;
      return { ...prev, documents: rest };
    });
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '', documents: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Patient name is required';
    else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) newErrors.name = 'Only letters and spaces allowed';

    if (!transferFrom) newErrors.transferFrom = 'Transfer From is required';
    if (!transferTo) newErrors.transferTo = 'Transfer To is required';
    if (!formData.diagnose.trim()) newErrors.diagnose = 'Diagnose is required';
    if (!formData.doctor) newErrors.doctor = 'Please select a doctor';
    if (!formData.clinic_no.trim()) newErrors.clinic_no = 'Clinic No is required';
    if (!formData.request_details.trim()) newErrors.request_details = 'Request details are required';
    if (!formData.bracelet_id.trim()) newErrors.bracelet_id = 'Bracelet ID is required';
    if (Object.keys(formData.documents).length === 0) newErrors.documents = 'At least one document is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const documentsBase64 = {};
      for (const [key, file] of Object.entries(formData.documents)) {
        if (file instanceof File) {
          documentsBase64[key] = await fileToBase64(file);
        }
      }

      const payload = {
        user_id: Number(sessionStorage.getItem("user_id")) || null,
        case_name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        nearest_medical_center: parseInt(transferTo),
        transfer_from_medical_center: parseInt(transferFrom),
        diagnose: formData.diagnose.trim(),
        triage_card: "Red",
        bracelet_id: formData.bracelet_id.trim(),
        doctor: parseInt(formData.doctor),
        doctor_name: formData.doctor_name,
        original_case_id: null,
        case_status: 2,
        nurse: Number(sessionStorage.getItem("user_id")) || null,
        nurse_name: sessionStorage.getItem("user_name") || "",
        doctor_approval_status: 0,
        clinic_no: formData.clinic_no.trim(),
        request_details: formData.request_details.trim(),
        escort_team: formData.escort_team,
        ...documentsBase64
      };

      await axiosInstance.post("/ems/tranfer/cases", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("auth_token") || sessionStorage.getItem("authToken")}`
        }
      });

      alert("Transfer case created successfully!");
      refreshList();
      onHide();
    } catch (err) {
      console.error("API Error:", err.response?.data || err);
      alert(err.response?.data?.message || "Failed to create transfer case");
    } finally {
      setIsLoading(false);
    }
  };
  

  const getDisplayFileName = (file) => {
    if (!file) return '';
    const name = file.name || '';
    return name.length > 20 ? `${name.substring(0, 17)}...${name.split('.').pop()}` : name;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered scrollable backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Add New Transfer Case</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="row g-3">
              {/*Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter patient name"
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              {/* Age */}
              <div className="col-md-6">
                <label className="form-label">Age</label>
                <input
                  type="number"
                  className="form-control"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="0"
                  max="120"
                />
              </div>

              {/* Gender */}
              <div className="col-md-6">
                <label className="form-label">Gender</label>
                <select className="form-select" name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              {/* Transfer From */}
              <div className="col-md-6">
                <label className="form-label">Transfer From </label>
                <select
                  className={`form-select ${errors.transferFrom ? 'is-invalid' : ''}`}
                  value={transferFrom}
                  onChange={(e) => {
                    setTransferFrom(e.target.value);
                    setFormData(prev => ({ ...prev, doctor: '', doctor_name: '' }));
                    setDoctors([]);
                    if (errors.transferFrom) setErrors(prev => ({ ...prev, transferFrom: '' }));
                  }}
                  disabled={['Hospital Representative (Nurse)', 'EMS Resource Manager'].includes(userRole) && medicalCenterId && medicalCenterId !== '0'}
                >
                  <option value="">Select Medical Center</option>
                  {medicalCenters.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.transferFrom && <div className="invalid-feedback">{errors.transferFrom}</div>}
              </div>

              {/* Transfer To */}
              <div className="col-md-6">
                <label className="form-label">Transfer To <span className="text-danger">*</span></label>
                <select
                  className={`form-select ${errors.transferTo ? 'is-invalid' : ''}`}
                  value={transferTo}
                  onChange={(e) => {
                    setTransferTo(e.target.value);
                    if (errors.transferTo) setErrors(prev => ({ ...prev, transferTo: '' }));
                  }}
                >
                  <option value="">Select Medical Center</option>
                  {filteredTransferTo.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.transferTo && <div className="invalid-feedback">{errors.transferTo}</div>}
              </div>

              {/* Diagnosis */}
              <div className="col-md-6">
                <label className="form-label">Diagnose <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.diagnose ? 'is-invalid' : ''}`}
                  name="diagnose"
                  value={formData.diagnose}
                  onChange={handleChange}
                  placeholder="Enter diagnose"
                />
                {errors.diagnose && <div className="invalid-feedback">{errors.diagnose}</div>}
              </div>

              {/* Doctor */}
              <div className="col-md-6">
                <label className="form-label">Doctor <span className="text-danger">*</span></label>
                <select
                  className={`form-select ${errors.doctor ? 'is-invalid' : ''}`}
                  value={formData.doctor}
                  onChange={handleDoctorChange}
                >
                  <option value="">Select Doctor</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {errors.doctor && <div className="invalid-feedback">{errors.doctor}</div>}
              </div>

              {/* Bracelet ID */}
              {/* Bracelet ID */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Bracelet ID <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                    name="bracelet_id"
                    value={formData.bracelet_id}
                    onChange={handleChange}
                  />
                  <i
                    className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowScanner(true)}
                  ></i>
                </div>
                {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
              </div>

              {/* Clinic No */}
              <div className="col-md-12">
                <label className="form-label">Clinic No <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.clinic_no ? 'is-invalid' : ''}`}
                  name="clinic_no"
                  value={formData.clinic_no}
                  onChange={handleChange}
                />
                {errors.clinic_no && <div className="invalid-feedback">{errors.clinic_no}</div>}
              </div>

              {/* Request Details */}
              <div className="col-12">
                <label className="form-label">Request Details <span className="text-danger">*</span></label>
                <textarea
                  className={`form-control ${errors.request_details ? 'is-invalid' : ''}`}
                  name="request_details"
                  value={formData.request_details}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe the transfer request..."
                />
                {errors.request_details && <div className="invalid-feedback">{errors.request_details}</div>}
              </div>

              {/* Escort Team */}
              <div className="col-12">
                <label className="form-label">Escort Team (Optional)</label>
                <div className="row g-2">
                  <div className="col-md-6">
                    <input type="text" className="form-control" name="escort_physician" value={formData.escort_team.escort_physician} onChange={handleEscortTeamChange} placeholder="Escort Physician" />
                  </div>
                  <div className="col-md-6">
                    <input type="text" className="form-control" name="escort_nurse" value={formData.escort_team.escort_nurse} onChange={handleEscortTeamChange} placeholder="Escort Nurse" />
                  </div>
                  <div className="col-md-6">
                    <input type="text" className="form-control" name="escort_paramedic" value={formData.escort_team.escort_paramedic} onChange={handleEscortTeamChange} placeholder="Escort Paramedic" />
                  </div>
                  <div className="col-md-6">
                    <input type="text" className="form-control" name="other" value={formData.escort_team.other} onChange={handleEscortTeamChange} placeholder="Other" />
                  </div>
                </div>
              </div>

              {/* Documents Upload */}
              <div className="col-md-12 mb-3">
              <label className="form-label">
                Documents Upload <span className="text-danger">*</span>
              </label>
              <small className="text-muted d-block mb-2">
                Allowed file types: PDF, JPG, JPEG, PNG, WEBP, JFIF. Maximum 3 files.
              </small>
              {errors.documents && <div className="text-danger mb-2">{errors.documents}</div>}

              {fileInputs.map((_, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="file"
                    className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                    onChange={(e) => handleFileChange(e, index)}
                    accept="image/jpeg,image/png,image/webp,image/jfif,application/pdf"
                  />
                  {formData.documents[`documents${index + 1}`] && (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{formData.documents[`documents${index + 1}`].name}</Tooltip>}>
                      <span className="input-group-text" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getDisplayFileName(formData.documents[`documents${index + 1}`])}
                      </span>
                    </OverlayTrigger>
                  )}
                  <div className="input-group-append d-flex align-items-center">
                    {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                      <button type="button" className="btn btn-sm btn-icon fs-5 me-2" onClick={addFileInput}>
                        <i className="ti ti-circle-plus text-success"></i>
                      </button>
                    )}
                    {fileInputs.length > 1 && (
                      <button type="button" className="btn btn-sm btn-icon fs-5" onClick={() => removeFileInput(index)}>
                        <i className="ti ti-trash text-danger"></i>
                      </button>
                    )}
                  </div>
                  {errors[`documents${index + 1}`] && <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>}
                </div>
              ))}
            </div>
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-danger" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* QR Scanner Modal */}
      {showScanner && (
        <Modal show={showScanner} onHide={() => setShowScanner(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Scan Bracelet QR Code</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div id="reader" style={{ width: '100%' }}></div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default AddTransferCaseModal;